import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, Clock, MapPin, User, Eye, Home, Building } from "lucide-react";
import { useInterviewerSlots, useSlotCounts } from "../../hooks/queries";
import Pagination from "../../components/ui/Pagination";
import { convertUTCToLocalTime } from "../../utils/timeUtils";

export default function InterviewerSlots() {
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 10;
  const navigate = useNavigate();

  // Use custom hook for data fetching with caching
  const {
    data: slotsData,
    isLoading,
    error,
    refetch
  } = useInterviewerSlots(currentPage, perPage);

  // Use slot counts API for dashboard cards
  const {
    data: countsData,
    isLoading: countsLoading,
    error: countsError
  } = useSlotCounts();

  const { slots = [], pagination = {} } = slotsData || {};
  const { total_slots = 0, location_counts = { home: 0, studio: 0 } } = countsData || {};

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        weekday: 'short',
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (error) {
      return dateString;
    }
  };

  // Updated formatTime function to handle UTC to local conversion
  const formatTime = (utcTimeString, dateString) => {
    return convertUTCToLocalTime(utcTimeString, dateString);
  };

  const getLocationIcon = (location) => {
    return location === 'home' ? <Home size={16} /> : <Building size={16} />;
  };

  const viewClientDetails = (slot) => {
    console.log('Viewing client details for slot:', slot);
    const userId = slot.user_id;
    if (userId) {
      navigate(`/user-questions/${userId}`);
    } else {
      console.error('No user ID found for slot:', slot);
    }
  };

  if (isLoading || countsLoading) {
    return (
      <div className="p-3 sm:p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-secondary rounded-lg w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-16 bg-secondary rounded-lg"></div>
            ))}
          </div>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-secondary rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || countsError) {
    return (
      <div className="p-3 sm:p-6">
        <div className="text-center">
          <p className="mb-4 text-foreground">{error?.message || countsError?.message}</p>
          <button 
            onClick={() => {
              refetch();
            }} 
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-foreground mb-2">Booked Sessions</h2>
        <p className="text-sm sm:text-base text-muted-foreground">
          Manage your assigned interview sessions ({pagination.total} total)
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-6">
        <div className="bg-secondary border border-secondary-border rounded-lg p-3 sm:p-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Calendar className="text-white" size={16} />
            </div>
            <div className="ml-3">
              <div className="text-lg font-bold text-foreground">{total_slots}</div>
              <div className="text-xs text-muted-foreground">Total Slots</div>
            </div>
          </div>
        </div>
        
        <div className="bg-secondary border border-secondary-border rounded-lg p-3 sm:p-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Home className="text-white" size={16} />
            </div>
            <div className="ml-3">
              <div className="text-lg font-bold text-foreground">
                {location_counts.home}
              </div>
              <div className="text-xs text-muted-foreground">Home Visits</div>
            </div>
          </div>
        </div>
        
        <div className="bg-secondary border border-secondary-border rounded-lg p-3 sm:p-4 sm:col-span-2 lg:col-span-1">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Building className="text-white" size={16} />
            </div>
            <div className="ml-3">
              <div className="text-lg font-bold text-foreground">
                {location_counts.studio}
              </div>
              <div className="text-xs text-muted-foreground">Studio Sessions</div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Card Layout (hidden on lg and up) */}
      <div className="lg:hidden space-y-4 mb-6">
        {slots.length === 0 ? (
          <div className="bg-secondary border border-secondary-border rounded-lg p-8 text-center">
            <Calendar className="mx-auto mb-2 text-muted-foreground" size={32} />
            <p className="text-muted-foreground">No sessions found</p>
          </div>
        ) : (
          slots.map((slot) => (
            <div key={slot.id} className="bg-secondary border border-secondary-border rounded-lg p-4">
              {/* Client Info */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <User className="text-primary" size={16} />
                  </div>
                  <div className="ml-3">
                    <div className="font-medium text-foreground">
                      {slot.user?.name || `Client #${slot.user_id}`}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {slot.user?.email || `ID: ${slot.user_id}`}
                    </div>
                  </div>
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                <div>
                  <div className="flex items-center text-muted-foreground mb-1">
                    <Calendar className="mr-2" size={12} />
                    <span className="text-xs">Date</span>
                  </div>
                  <div className="text-foreground font-medium">{formatDate(slot.slot_date)}</div>
                </div>
                
                <div>
                  <div className="flex items-center text-muted-foreground mb-1">
                    <Clock className="mr-2" size={12} />
                    <span className="text-xs">Time</span>
                  </div>
                  <div className="text-foreground font-medium">
                    {formatTime(slot.slot_time, slot.slot_date)}
                    {slot.end_time && (
                      <span className="text-xs text-muted-foreground ml-1">
                        - {formatTime(slot.end_time, slot.slot_date)}
                      </span>
                    )}
                  </div>
                </div>

                <div>
                  <div className="flex items-center text-muted-foreground mb-1">
                    {getLocationIcon(slot.location)}
                    <span className="ml-2 text-xs">Location</span>
                  </div>
                  <div className="text-foreground font-medium capitalize">{slot.location}</div>
                </div>

                <div>
                  <div className="flex items-center text-muted-foreground mb-1">
                    <Clock className="mr-2" size={12} />
                    <span className="text-xs">Duration</span>
                  </div>
                  <div className="text-foreground font-medium">
                    {slot.duration ? `${slot.duration} min` : 'N/A'}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="pt-3 border-t border-secondary-border">
                <button
                  onClick={() => viewClientDetails(slot)}
                  className="text-primary hover:text-primary/80 text-sm font-medium flex items-center px-3 py-1.5 border border-primary/20 rounded-md hover:bg-primary/5"
                >
                  <Eye className="mr-1" size={14} />
                  View Questions
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop Table Layout (hidden on mobile/tablet) */}
      <div className="hidden lg:block bg-secondary border border-secondary-border rounded-lg overflow-hidden mb-6">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-secondary border-b border-secondary-border">
              <tr>
                <th className="text-left py-3 px-4 font-semibold text-foreground">Client</th>
                <th className="text-left py-3 px-4 font-semibold text-foreground">Date</th>
                <th className="text-left py-3 px-4 font-semibold text-foreground">Time (Local)</th>
                <th className="text-left py-3 px-4 font-semibold text-foreground">Location</th>
                <th className="text-left py-3 px-4 font-semibold text-foreground">Duration</th>
                <th className="text-left py-3 px-4 font-semibold text-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-border">
              {slots.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-8">
                    <Calendar className="mx-auto mb-2 text-muted-foreground" size={32} />
                    <p className="text-muted-foreground">No sessions found</p>
                  </td>
                </tr>
              ) : (
                slots.map((slot) => (
                  <tr key={slot.id} className="hover:bg-secondary/50">
                    <td className="py-3 px-4">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                          <User className="text-primary" size={14} />
                        </div>
                        <div className="ml-3">
                          <div className="font-medium text-foreground">
                            {slot.user?.name || `Client #${slot.user_id}`}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {slot.user?.email || `ID: ${slot.user_id}`}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center text-sm">
                        <Calendar className="mr-2 text-primary" size={14} />
                        <span className="text-foreground">{formatDate(slot.slot_date)}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center text-sm">
                        <Clock className="mr-2 text-primary" size={14} />
                        <span className="text-foreground">{formatTime(slot.slot_time, slot.slot_date)}</span>
                        {slot.end_time && (
                          <span className="text-xs text-muted-foreground ml-1">
                            - {formatTime(slot.end_time, slot.slot_date)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center text-sm">
                        <div className="text-primary mr-2">
                          {getLocationIcon(slot.location)}
                        </div>
                        <span className="text-foreground capitalize">{slot.location}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-sm text-foreground">
                        {slot.duration ? `${slot.duration} min` : 'N/A'}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <button
                        onClick={() => viewClientDetails(slot)}
                        className="text-primary hover:text-primary/80 text-sm font-medium flex items-center"
                      >
                        <Eye className="mr-1" size={14} />
                        View Questions
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Pagination */}
      <div className="bg-secondary border border-secondary-border rounded-lg">
        <div className="border-t border-secondary-border">
          <Pagination
            currentPage={pagination.current_page || 1}
            lastPage={pagination.last_page || 1}
            perPage={pagination.per_page || 10}
            total={pagination.total || 0}
            onPageChange={handlePageChange}
          />
        </div>
      </div>
    </div>
  );
} 